<template>
    <quote-info
        :quote-id="quote.id"
        v-model="showMoreInfo"
        :isPayMonthly="isPayMonthly ?? false"
        :showAnnualAndMonthlyCosts="false"
    />
    <asli-form v-slot="{ submit }" @submitted="goNext" @failed="onFail">
        <asli-form-section
            v-if="quote.reference"
            heading-level="h2"
            :title="isOfflineQuote ? `We've Retrieved Your Quote` : `Here's Your Indicative Price`"
            :reference="quote.reference"
        >
            <p v-if="!isOfflineQuote">
                Great news {{ firstName }}! Based on the details you have provided, the below are our best prices
                available! Please review the cover available as detailed below, choose your payment frequency, and
                continue to make your payment. If there are types of cover you would like that aren't listed below or on
                the next screen, please give us a call, as we may have an alternative provider option for you.
            </p>
            <p v-else>
                Please review your quote and cover details below, choose your payment frequency, and continue to make
                your payment. If you obtained or amended your quote in our call centre & you need to make any further
                changes, please give us a call and we will be happy to help.
            </p>
            <p class="my-[15px]">Your quote is valid until {{ dateValidTo }}.</p>
            <asli-single-quote-display
                v-model="isPayMonthly"
                :quote="mappedQuote"
                :coverDetails="risk.cover_details"
                site="Home"
                :isOfflineQuote="isOfflineQuote"
                @more-info-clicked="showMoreInfo = true"
            />
            <div class="my-7"></div>
            <tailor-your-cover
                v-if="!isOfflineQuote"
                ref="tailorYourCoverRef"
                @tailor-field-changed="tailorFormHasChanges = $event"
                :key="'tailor-cover-' + proposal.uuid"
                :proposal="proposal"
            />
            <div class="my-7"></div>
            <template v-if="hasBuildingsCover && !isOfflineQuote">
                <h2 class="text-[28px] leading-normal text-section-header font-extrabold mb-2">Mortgage Details</h2>
                <asli-radio
                    id="has_interested_parties"
                    name="has_interested_parties"
                    rules="required"
                    v-model="risk.has_interested_parties"
                    label="Do you have a mortgage?"
                    return-boolean
                    :tooltipText="MORTGAGE_DETAILS.has_mortgage"
                    is-yes-no-option
                    :options="lists?.yes_no"
                    @change="commitRisk(true)"
                />
                <asli-dropdown
                    v-if="risk.has_interested_parties"
                    id="interested_parties"
                    name="interested_parties"
                    rules="required:has_mortgage"
                    v-model="interestedParty"
                    label="Who is your Mortgage provider?"
                    :options="lists.interested_parties"
                    placeholder="Please select an option"
                    large
                    @option-selected="commitRisk(true)"
                />
                <div class="my-7"></div>
            </template>
            <div class="my-7"></div>
            <div v-if="!isOfflineQuote">
                <h2 class="text-[28px] text-section-header font-extrabold mb-1">What's Covered</h2>
                <div class="mb-6">
                    <paragraph>
                        In addition to the cover that you would expect such as
                        <span class="font-bold text-section-header">{{ expectedCoverSummary }}</span
                        >, the below is a summary of some of the other {{ insurer }} policy benefits. You should refer
                        to the applicable Home Insurance Policy Booklet, and within the documentation we will issue to
                        you, for full details.
                    </paragraph>
                </div>
                <asli-panel
                    v-if="floodExclusionEndorsementIsApplied"
                    background="bg-panel-primary"
                    class="flex items-center gap-4 mb-7 rounded-[5px] p-5"
                >
                    <img src="@/assets/warning.svg" alt="Warning" class="w-9" />
                    <p class="font-bold text-darkGrey">
                        Please be advised that this policy does not cover any loss or damage caused by flood
                    </p>
                </asli-panel>
                <whats-covered-list v-if="benefits" :quote="quote" :benefits="benefits" />
            </div>
            <h2 class="text-[28px] text-section-header font-extrabold mb-1">Review Your Details</h2>
            <summary-accordion :pay-monthly="isPayMonthly" />
        </asli-form-section>
        <quote-button-bar
            v-if="!isOfflineQuote"
            :quote="quote"
            :is-monthly="isPayMonthly"
            :premiums="{
                monthly: quote.monthly_total_payable,
                annual: quote.premium
            }"
            :recalculate-flag="recalculateFlag"
            @go-back="goBack"
            @go-next="submit"
        />
        <quote-button-bar
            v-else
            :quote="quote"
            :is-monthly="isPayMonthly"
            :premiums="{
                monthly: quote.monthly_total_payable,
                annual: quote.premium
            }"
            :recalculate-flag="recalculateFlag"
            @go-next="submit"
            :show-back-button="false"
        />
    </asli-form>
</template>
<script lang="ts" setup>
import { PropType, computed, onMounted, ref } from "vue";
import { useRouter, onBeforeRouteLeave } from "vue-router";
import {
    AsliFormSection,
    AsliSingleQuoteDisplay,
    AsliRadio,
    AsliDropdown,
    AsliForm,
    AsliPanel,
    isRepeaterItemEmpty
} from "@mbs-npm/anpost-ui";
import { FORM_MODE, ROUTE_NAMES } from "@/router/types";
import { useMainStore } from "@/stores/main";
import { useProposalStore } from "@/stores/risk/risk";
import Paragraph from "@/components/Paragraph.vue";
import WhatsCoveredList from "./SingleQuote/WhatsCoveredList.vue";
import QuoteButtonBar from "@/components/ButtonBars/QuoteButtonBar.vue";
import { FormRouteName } from "@/router/utils";
import QuoteInfo from "@/components/QuoteInfo.vue";
import { useQuoteForDisplay } from "@/hooks/useQuoteForDisplay";
import { goToError } from "@/utils/error-handling";
import { Quote } from "@/stores/risk/types/types";
import { useListStore } from "@/stores/lists/lists";
import TailorYourCover from "./TailorYourCover.vue";
import { MORTGAGE_DETAILS } from "@/utils/helpText";
import SummaryAccordion from "./SingleQuote/Summary/SummaryAccordion.vue";
import { formatDate, isInsurer, sanitizeCoverDetails } from "@/utils";
import { useDetectFormChanges } from "@/hooks/useDetectFormChanges";
import { EndorsementCode, INSURER_NAMES } from "@/static/constants";
import { cloneDeep } from "lodash";
import { APP_STEP } from "@/stores/types";

const mainStore = useMainStore();
mainStore.setAppStep(APP_STEP.QUOTES);

const proposalStore = useProposalStore();
const listStore = useListStore();
const lists = computed(() => listStore?.lists);
const proposal = computed(() => proposalStore.proposal);
const risk = computed(() => proposalStore.proposal.risk);
const router = useRouter();

const tailorYourCoverRef = ref<InstanceType<typeof TailorYourCover> | null>(null);

const tailorFormHasChanges = ref<boolean>(false);
const formHasChanges = ref<boolean>(false);

const interestedParty = ref<string | null>(null);

defineProps({
    quotes: {
        type: Array as PropType<Array<Quote>>,
        required: true
    }
});

const firstName = computed(() => proposal.value.risk.proposer.personal_details.first_name);

const quote = computed(() => proposal.value.quote);
const mappedQuote = computed(() => useQuoteForDisplay(quote.value));

const insurer = computed(() => quote.value?.insurer_name);

const isPayMonthly = ref<boolean>(proposal.value.is_pay_monthly);
const showMoreInfo = ref<boolean>(false);
const isOfflineQuote = computed(() => proposal.value.recalled_from_offline);

const hasBuildingsCover = computed(() => {
    return (
        proposal.value.risk?.cover_details.buildings_cover_amount !== null &&
        proposal.value.risk?.cover_details.buildings_cover_amount > 0
    );
});
const recalculateFlag = computed(() => tailorFormHasChanges.value || formHasChanges.value);
function buildFormValues() {
    const result: {
        has_interested_parties: boolean | null;
        interested_parties?: string | null;
    } = {
        has_interested_parties: risk.value.has_interested_parties
    };

    if (risk.value.has_interested_parties) {
        result.interested_parties = interestedParty.value;
    }

    return result;
}

const { checkFormHasChanges, updateFormValues, resetInitialValues, initialiseValues } = useDetectFormChanges();

const commitRisk = (setFlag: boolean) => {
    updateFormValues(buildFormValues());
    if (setFlag) {
        formHasChanges.value = checkFormHasChanges();
    }
    proposalStore.patchInterestedParties(risk.value.has_interested_parties, interestedParty.value);
};

const benefits = computed(() => {
    return quote.value?.selectable_benefits;
});

const floodExclusionEndorsementIsApplied = computed<boolean>(
    () => !!proposalStore.getEndorsementByCode(EndorsementCode.FloodExclusion)
);

const expectedCoverSummary = computed<string>(
    () => `Fire, Theft${floodExclusionEndorsementIsApplied.value ? "" : ", Flood"} and Storm damage`
);

const dateValidTo = computed(() =>
    formatDate(
        isInsurer(quote.value, INSURER_NAMES.Allianz) ? risk.value.policy_details.start_date : quote.value.date_valid_to
    )
);

function getSportsEquipmentAmount(coverDetails) {
    if (coverDetails.sports_equipment_amount != null) {
        const strValue = coverDetails.sports_equipment_amount.toString().trim();
        if (strValue) return parseInt(strValue.replace(/,/g, ""));
    }
    return null;
}

onMounted(() => {
    if (!quote.value.reference) goToError(proposalStore.proposal.uuid as string);

    if (risk.value.interested_parties && risk.value.interested_parties.length > 0) {
        interestedParty.value = risk.value.interested_parties[0].code;
    }
    initialiseValues(buildFormValues());
});

async function recalculateQuote() {
    await proposalStore.recalculateQuote(true, isPayMonthly.value);
    formHasChanges.value = false;
    tailorFormHasChanges.value = false;
    resetInitialValues();
    tailorYourCoverRef?.value?.doReset();
    return;
}

async function checkRepeater() {
    if (tailorYourCoverRef.value) {
        return await tailorYourCoverRef.value.validateRepeater();
    }
    return true;
}

async function onFail() {
    const isRepeaterValid = await checkRepeater();

    if (isRepeaterValid === false) {
        return;
    }
}

async function setPaymentChoice() {
    if (isPayMonthly.value !== proposal.value.is_pay_monthly) {
        mainStore.setIsMonthly(isPayMonthly.value);
        await proposalStore.UpdatePaymentChoice(isPayMonthly.value);
    }
}

function goBack() {
    const coverDetails = risk.value.cover_details;
    coverDetails.sports_equipment_amount = getSportsEquipmentAmount(coverDetails);
    if (!risk.value.interested_parties.length) {
        proposalStore.patchInterestedParties(false);
    }
    const updatedCoverDetails = sanitizeCoverDetails(coverDetails);
    if (updatedCoverDetails !== coverDetails) proposalStore.patchCoverDetails(updatedCoverDetails);
    setPaymentChoice();
    router.push({
        name: FormRouteName(ROUTE_NAMES.YourCover, FORM_MODE.EDIT),
        query: {
            uuid: proposalStore.proposal.uuid
        }
    });
}

async function goNext() {
    const isRepeaterValid = await checkRepeater();

    if (isRepeaterValid === false) {
        return;
    }

    const coverDetails = risk.value.cover_details;
    if (!risk.value.cover_details.has_specified_items) {
        risk.value.cover_details.specified_items = [];
    }

    coverDetails.sports_equipment_amount = getSportsEquipmentAmount(coverDetails);

    proposalStore.patchCoverDetails(coverDetails);

    if (!risk.value.has_interested_parties) {
        interestedParty.value = null;
    }

    commitRisk(true);

    if (recalculateFlag.value) {
        const currentProposal = cloneDeep(proposal.value);

        proposalStore.setProposal(currentProposal);

        await recalculateQuote();
        return;
    }

    try {
        mainStore.setLoadingStatePopupText("Please wait...");
        mainStore.setLoadingState(true);
        await setPaymentChoice();
        await proposalStore.selectQuote(quote.value.id);
        const routeName = isOfflineQuote.value ? ROUTE_NAMES.Marketing : ROUTE_NAMES.QuoteConfirm;
        await router.push({
            name: routeName
        });
    } finally {
        mainStore.setLoadingState(false);
    }
}

onBeforeRouteLeave((to, from, next) => {
    showMoreInfo.value = false;
    next();
});
</script>
